import React, { useEffect, useRef, useState } from 'react';
import CPageModule from '../../../components/CPageModule';
import Page from '../../../components/layout/Page';
import { updateStateAttribute } from '../../../helpers/helper';
import CIndexTotalReturn from './CIndexTotalReturn';
import CSectorDetails from './CSectorDetails';
import CSectorDistribution from './CSectorDistribution';
import CSectorTotalReturn from './CSectorTotalReturn';
import PageParameterSection from './PageParameterSection';
import SvSectorPerformanceServices from '../../../services/SvSectorPerformance';
import { type PlotlyResponse } from '../../../models/PlotlyModel';
import { type TPandasTable } from '../../../models/TPandasTable';
import { type IResponse } from '../../../models/IResponse';
import { type TLocales } from '../../../models/TLocales';
import { useAppSelector } from '../../../hooks/hooks';
import { type ISectorPerformanceParameters } from '../../../models/ISectorPerformanceParameters';
import { type ISectorDetailArgs } from '../../../models/ISectorDetailArgs';
import dayjs from 'dayjs';
import CLocalizedText from '../../../components/CLocalizedText';
import {SvUsageTrackingServices} from '../../../services/SvUsageTrackingServices';
import {useUsageTrackingInfo} from '../../../hooks/useUsageTrackingInfo';
import {type IUsageTrackingInfo} from '../../../models/usage-tracking/IUsageTracking';
import {EAppType} from '../../../enums/AppType';

interface State {
  parameters: ISectorPerformanceParameters;
  initialLoaded: boolean;
  sectorDistributionPlot: IResponse<PlotlyResponse>;
  sectorDistributionTable: IResponse<TPandasTable>;
  cIndexTotalReturnPlot?: IResponse<PlotlyResponse>;
  cIndexTotalReturnTable?: IResponse<TPandasTable>;
  cSectorTotalReturnPlot?: IResponse<PlotlyResponse>;
  cSectorTotalReturnTable?: IResponse<TPandasTable>;
  sectorDetailPlots: IResponse<{
    sector_plot: PlotlyResponse;
    all_plot: PlotlyResponse;
  }>;
  sectorDetailTables: IResponse<{
    monthly_returns: TPandasTable;
    monthly_performance: TPandasTable;
    annual_performance: TPandasTable;
  }>;
  targetScrollProgress?: number;
}

const defaultParams: ISectorPerformanceParameters = {
  noCompanies: 500,
  startDate: '2010-01-01',
  endDate: dayjs().format('YYYY-MM-DD'), // Today's date
  tableDate: dayjs().format('YYYY-MM-DD'), // Today's date
  selection: 'mcap',
  interval: 'quarterly',
  weight: 'mcap',
  minDate: dayjs('2010-01-01').format('YYYY-MM-DD'), // Today's date
  maxDate: dayjs().format('YYYY-MM-DD'), // Today's date
  year: dayjs().year() // Current year
};

const NUMBER_OF_YEARS = dayjs().year() - 2000;

export default function PSectorPerformanceIndex() {
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const { locale } = useAppSelector((s) => s.dashboard) as { locale: TLocales };
  const usageTrackingParams: IUsageTrackingInfo = useUsageTrackingInfo(EAppType.SectorPerformance);
  const [state, setState] = useState<State>({
    parameters: defaultParams,
    initialLoaded: false,
    cIndexTotalReturnPlot: {data: undefined, state: {isLoading: false, isError: false}},
    cIndexTotalReturnTable: {data: undefined, state: {isLoading: false, isError: false}},
    cSectorTotalReturnPlot: {data: undefined, state: {isLoading: false, isError: false}},
    cSectorTotalReturnTable: {data: undefined, state: {isLoading: false, isError: false}},
    sectorDistributionPlot: {
      state: { isLoading: true, isError: false },
      data: undefined
    },
    sectorDistributionTable: {
      state: { isLoading: true, isError: false },
      data: undefined
    },
    sectorDetailPlots: { state: { isLoading: true, isError: false }, data: undefined },
    sectorDetailTables: { state: { isLoading: true, isError: false }, data: undefined }
  });
  const {dashboardOutletDimensions} = useAppSelector((state) => state.dashboard);

  const handleChange = (partial: Partial<ISectorPerformanceParameters>) => {
    let next: Partial<ISectorPerformanceParameters> = { ...partial };

    if (partial?.endDate !== undefined) {
      next = {
        ...partial, // In case the partial has other keys
        tableDate: partial.endDate // Override the sector distribution table date every time the "end date" changes.
      };
    }

    updateStateAttribute<State>({
      parameters: {
        ...state.parameters,
        ...next as ISectorPerformanceParameters
      }}, setState);
  };

  const fetchSectorDistributionPlot = async (startDate: string, endDate: string) => {
    updateStateAttribute<State>({
      sectorDistributionPlot: {
        ...state.sectorDistributionPlot,
        state: { ...state.sectorDistributionPlot.state, isLoading: true }
      }
    }, setState);

    const res = await SvSectorPerformanceServices.getSectorDistributionPlot(startDate, endDate);

    if (res?.wasSuccessful()) {
      updateStateAttribute<State>({
        sectorDistributionPlot: {
          ...state.sectorDistributionPlot,
          state: { isLoading: false, isError: false },
          data: res.getData()
        }
      }, setState);
    } else {
      updateStateAttribute<State>({
        sectorDistributionPlot: {
          ...state.sectorDistributionPlot,
          state: {
            isLoading: false,
            isError: true,
            strError: res.getErrorString?.(),
            objError: res.getErrorObj?.()
          }
        }
      }, setState);
    }
  };

  const fetchSectorDistributionTable = async (dateForTable: string) => {
    updateStateAttribute<State>({
      sectorDistributionTable: {
        ...state.sectorDistributionTable,
        state: { ...state.sectorDistributionTable.state, isLoading: true }
      }
    }, setState);

    const res = await SvSectorPerformanceServices.getSectorDistributionTable(dateForTable);

    if (res.wasSuccessful()) {
      updateStateAttribute<State>({
        sectorDistributionTable: {
          ...state.sectorDistributionTable,
          state: { isLoading: false, isError: false },
          data: res.getData()
        }
      }, setState);
    } else {
      updateStateAttribute<State>({
        sectorDistributionTable: {
          ...state.sectorDistributionTable,
          state: {
            isLoading: false,
            isError: true,
            strError: res.getErrorString?.(),
            objError: res.getErrorObj?.()
          }
        }
      }, setState);
    }
  };

  const fetchCIndexTotalReturnsPlot = async () => {
    updateStateAttribute<State>({
      cIndexTotalReturnPlot: {...state.cIndexTotalReturnPlot, state: {isLoading: true, isError: false}},
    }, setState);

    const index = `${state.parameters.interval}_${state.parameters.selection}_${state.parameters.noCompanies}`;

    const plotRes = await SvSectorPerformanceServices.getIndexTotalReturnPlot(state.parameters.startDate, state.parameters.endDate, state.parameters.weight, index);

    if (!plotRes.hasError()) {
      updateStateAttribute<State>({
        cIndexTotalReturnPlot: {
          data: plotRes.getData(),
          state: {isLoading: false, isError: false},
        }
      }, setState);
    } else {
      updateStateAttribute<State>({
        cIndexTotalReturnPlot: {
          ...state.cIndexTotalReturnPlot,
          state: {isLoading: false, isError: plotRes.hasError()},
        }
      }, setState);
    }
  };

  const fetchCIndexTotalReturnsTable = async () => {
    updateStateAttribute<State>({
      cIndexTotalReturnTable: {...state.cIndexTotalReturnTable, state: {isLoading: true, isError: false}},
    }, setState);

    const index = `${state.parameters.interval}_${state.parameters.selection}_${state.parameters.noCompanies}`;

    const tableRes = await SvSectorPerformanceServices.getIndexTotalReturnTable(state.parameters.startDate, state.parameters.endDate, state.parameters.weight, index);

    if (!tableRes.hasError()) {
      updateStateAttribute<State>({
        cIndexTotalReturnTable: {
          data: tableRes.getData().table,
          state: {isLoading: false, isError: false},
        }
      }, setState);
    } else {
      updateStateAttribute<State>({
        cIndexTotalReturnTable: {
          ...state.cIndexTotalReturnTable,
          state: {isLoading: false, isError: tableRes.hasError()},
        }
      }, setState);
    }
  };

  const fetchCSectorTotalReturnsPlot = async () => {

    updateStateAttribute<State>({
      cSectorTotalReturnPlot: {...state.cSectorTotalReturnPlot, state: {isLoading: true, isError: false}},
    }, setState);

    const index = `${state.parameters.interval}_${state.parameters.selection}_${state.parameters.noCompanies}`;

    const plotRes = await SvSectorPerformanceServices.getSectorTotalReturnPlot(state.parameters.startDate, state.parameters.endDate, state.parameters.weight, index);

    if (!plotRes.hasError()) {
      updateStateAttribute<State>({
        cSectorTotalReturnPlot: {
          data: plotRes.getData(),
          state: {isLoading: false, isError: false},
        }
      }, setState);
    } else {
      updateStateAttribute<State>({
        cSectorTotalReturnPlot: {
          ...state.cSectorTotalReturnPlot,
          state: {isLoading: false, isError: plotRes.hasError()},
        }
      }, setState);
    }
  };

  const fetchCSectorTotalReturnsTable = async () => {

    updateStateAttribute<State>({
      cSectorTotalReturnTable: {...state.cSectorTotalReturnTable, state: {isLoading: true, isError: false}},
    }, setState);

    const index = `${state.parameters.interval}_${state.parameters.selection}_${state.parameters.noCompanies}`;

    const tableRes = await SvSectorPerformanceServices.getSectorTotalReturnTable(state.parameters.startDate, state.parameters.endDate, state.parameters.weight, index);

    if (!tableRes.hasError()) {
      updateStateAttribute<State>({
        cSectorTotalReturnTable: {
          data: tableRes.getData(),
          state: {isLoading: false, isError: false},
        }
      }, setState);
    } else {
      updateStateAttribute<State>({
        cSectorTotalReturnTable: {
          ...state.cSectorTotalReturnTable,
          state: {isLoading: false, isError: tableRes.hasError()},
        }
      }, setState);
    }

  };

  const fetchSectorDetailTables = async (args: ISectorDetailArgs) => {
    let sector = args.sector;
    if (!sector) {
      const firstSector = Object.keys(state.sectorDistributionTable?.data ?? {}).find((name) => name !== 'All Sectors');
      if (firstSector) {
        sector = firstSector;
      }
    }
    updateStateAttribute<State>(
      {
        sectorDetailTables: {
          ...state.sectorDetailTables,
          state: { ...state.sectorDetailTables.state, isLoading: true }
        }
      },
      setState
    );

    const res = await SvSectorPerformanceServices.getSectorDetailTables({ ...args, sector });

    if (res?.wasSuccessful()) {
      updateStateAttribute<State>(
        {
          sectorDetailTables: {
            ...state.sectorDetailTables,
            state: { isLoading: false, isError: false },
            data: res.getData()
          }
        },
        setState
      );
    } else {
      updateStateAttribute<State>(
        {
          sectorDetailTables: {
            ...state.sectorDetailTables,
            state: {
              isLoading: false,
              isError: true,
              strError: res.getErrorString?.(),
              objError: res.getErrorObj?.()
            }
          }
        },
        setState
      );
    }
  };

  const fetchSectorDetailPlots = async (args: ISectorDetailArgs) => {
    let sector = args.sector;
    if (!sector) {
      const firstSector = Object.keys(state.sectorDistributionTable?.data ?? {}).find((name) => name !== 'All Sectors');
      if (firstSector) {
        sector = firstSector;
      }
    }
    updateStateAttribute<State>(
      {
        sectorDetailPlots: {
          ...state.sectorDetailPlots,
          state: { ...state.sectorDetailPlots.state, isLoading: true }
        }
      },
      setState
    );

    const res = await SvSectorPerformanceServices.getSectorDetailPlots({ ...args, sector });

    if (res?.wasSuccessful()) {
      updateStateAttribute<State>(
        {
          sectorDetailPlots: {
            ...state.sectorDetailPlots,
            state: { isLoading: false, isError: false },
            data: res.getData()
          }
        },
        setState
      );
    } else {
      updateStateAttribute<State>(
        {
          sectorDetailPlots: {
            ...state.sectorDetailPlots,
            state: {
              isLoading: false,
              isError: true,
              strError: res.getErrorString?.(),
              objError: res.getErrorObj?.()
            }
          }
        },
        setState
      );
    }
  };

  const fetchAllData = async () => {
    await Promise.all([
      fetchCIndexTotalReturnsPlot(),
      fetchCIndexTotalReturnsTable(),
      fetchCSectorTotalReturnsPlot(),
      fetchCSectorTotalReturnsTable(),
    ]);
  };

  const onReset = async () => {
    const today = dayjs().format('YYYY-MM-DD');
    updateStateAttribute<State>({parameters: {...defaultParams, endDate: today, tableDate: today, maxDate: today}}, setState);
  };

  useEffect(() => {
    const dist = state.sectorDistributionTable;
    const distState = dist?.state;

    const setSharedError = () => {
      const shared = {
        isLoading: false,
        isError: true,
        strError: distState?.strError,
        objError: distState?.objError,
      };

      updateStateAttribute <State>(
        {
          sectorDetailPlots: { ...state.sectorDetailPlots, state: shared },
          sectorDetailTables: { ...state.sectorDetailTables, state: shared },
        },
        setState
      );
    };

    if (distState?.isError && dist?.data === undefined) {
      setSharedError();
    } else if (!state.initialLoaded && dist?.data) {
      (async () => {
        try {
          const params = {
            sector: state.parameters.sector,
            year: state.parameters.year,
            weight: state.parameters.weight,
            interval: state.parameters.interval,
            selection: state.parameters.selection,
            noCompanies: state.parameters.noCompanies,
          };

          await Promise.all([
            fetchSectorDetailPlots(params),
            fetchSectorDetailTables(params),
          ]);
        } finally {
          updateStateAttribute<State>({ initialLoaded: true }, setState);
        }
      })();
    }
  }, [
    state.initialLoaded,
    state.sectorDistributionTable?.data,
    state.sectorDistributionTable?.state?.isError,
  ]);

  const parameterBar = (
    <PageParameterSection
      noCompanies={state.parameters.noCompanies}
      startDate={state.parameters.startDate}
      endDate={state.parameters.endDate}
      selection={state.parameters.selection}
      interval={state.parameters.interval}
      weight={state.parameters.weight}
      onParamChange={handleChange}
      onReset={onReset}
      maxDate={state.parameters.maxDate}
      minDate={state.parameters.minDate}
    />
  );

  useEffect(() => {
    if (!state.initialLoaded) return;

    (async () => {
      try {
        await Promise.all([
          fetchSectorDetailPlots({
            sector: state.parameters.sector,
            year: state.parameters.year,
            weight: state.parameters.weight,
            interval: state.parameters.interval,
            selection: state.parameters.selection,
            noCompanies: state.parameters.noCompanies
          }),
          fetchSectorDetailTables({
            sector: state.parameters.sector,
            year: state.parameters.year,
            weight: state.parameters.weight,
            interval: state.parameters.interval,
            selection: state.parameters.selection,
            noCompanies: state.parameters.noCompanies
          })
        ]);
      } finally {
        updateStateAttribute<State>({ initialLoaded: true }, setState);
      }
    })();
  }, [state.parameters.weight, state.parameters.interval, state.parameters.selection, state.parameters.noCompanies, state.parameters.year,state.parameters.sector,locale]);


  useEffect(() => {
    (async () => {
      await fetchSectorDistributionTable(state.parameters.tableDate);
    })();
  }, [state.parameters.tableDate, locale]);

  useEffect(() => {
    (async () => {
      await fetchSectorDistributionPlot(state.parameters.startDate, state.parameters.endDate);
    })();
  }, [state.parameters.startDate, state.parameters.endDate, locale]);

  useEffect(() => {
    (async () => {
      await fetchAllData();
    })();
  }, [state.parameters.startDate, state.parameters.endDate, state.parameters.noCompanies, state.parameters.interval, state.parameters.selection, state.parameters.weight, locale]);

  useEffect(() => {
    const targetScrollProgress = stickyHeaderRef.current?.getBoundingClientRect().top;
    if ((targetScrollProgress ?? 0) > 0) {
      updateStateAttribute<State>({ targetScrollProgress: (targetScrollProgress ?? 0) - 16 }, setState);
    }
  }, [dashboardOutletDimensions.w]);

  useEffect(() => {
    (async () => {
      await SvUsageTrackingServices.createUsageTrackingRecord(usageTrackingParams);
    })();
  }, []);

  return (
    <Page stickyHeader={parameterBar} padStickyHeader targetScrollProgress={state.targetScrollProgress}>
      <CPageModule>
        <h1>
          <CLocalizedText dictKey={'sectorPerformance.title'}/>
        </h1>
        <CLocalizedText dictKey={'sectorPerformance.desc'}/>
        <div ref={stickyHeaderRef}>
          {parameterBar}
        </div>
      </CPageModule>
      <CIndexTotalReturn plot={state.cIndexTotalReturnPlot} table={state.cIndexTotalReturnTable}/>
      <CSectorTotalReturn plot={state.cSectorTotalReturnPlot} table={state.cSectorTotalReturnTable}/>
      <CSectorDistribution
        initialEndDate={state.parameters.tableDate}
        initialStartDate={state.parameters.startDate}
        sectorDistributionPlot={state.sectorDistributionPlot}
        sectorDistributionTable={state.sectorDistributionTable}
        onParamChange={handleChange}
        parameters={state.parameters}
        minDate={state.parameters.minDate}
        maxDate={state.parameters.maxDate}
      />
      <CSectorDetails
        sectorDetailPlots={state.sectorDetailPlots}
        sectorDetailTables={state.sectorDetailTables}
        sectorDistributionTable={state.sectorDistributionTable}
        year={state.parameters.year}
        sector={state.parameters.sector}
        onParamChange={handleChange}
        noOfYears={NUMBER_OF_YEARS}
      />
    </Page>
  );
}